/**
 * Locale config loader. Mirrors `createBrandLoader` plus snake→camel
 * normalization, per-field sanitization, and a boot-time race helper that
 * caps the await with a timeout + abort.
 *
 * Failure policy matches `brand-loader.js`: API errors are swallowed silently
 * and `LOCALE_DEFAULTS` are used. Boot must never block on a config endpoint.
 *
 * Module declaration order (function declarations hoist; const arrows do
 * not — keep this order intentional):
 *   1. LOCALE_DEFAULTS
 *   2. Time-format heuristic (TIME_TOKEN_RE, looksLikeTimeFormat)
 *   3. Sanitizers + ensureCompleteLocaleConfig
 *   4. normalizeApiLocale
 *   5. createLocaleLoader
 *   6. loadLocaleForBoot
 */

import { stripDateTokens } from './format-date.js';

/**
 * Universal fallback locale config. Frozen so accidental mutation by a caller
 * cannot poison subsequent loads.
 *
 * @type {Readonly<import('../../contracts/locale.js').LocaleConfig>}
 */
export const LOCALE_DEFAULTS = Object.freeze({
  locale: 'en-US',
  // displayLocale defaults to 'en' so the calendar header reads "March 2024"
  // / "Mon Tue ..." regardless of the locale's date pattern. Override per row
  // when a non-English calendar header is desired.
  displayLocale: 'en',
  dateFormat: 'yyyy-MM-dd',
  timeFormat: 'HH:mm',
  datetimeFormat: 'yyyy-MM-dd HH:mm',
  firstDayOfWeek: 1,
  rtl: false,
});

const MIN_TIMEOUT_MS = 1;
const MAX_TIMEOUT_MS = 60_000;

// ---------------------------------------------------------------------------
// Suspicious-token heuristic — warn if dateFormat looks like it has time
// tokens. Operates on the post-tokenization residue so literal labels like
// "(local)" or "(shift)" don't false-positive.
//
// Sequence-aware: matches a known time-token RUN bordered by start/end or
// a non-letter char. Without "match the whole run" semantics, a single-char
// regex misses `HH` because each `H` is adjacent to another letter.
// Avoids lookbehind for older-Safari compatibility; uses a leading capture
// group instead.
// ---------------------------------------------------------------------------

const TIME_TOKEN_RE = /(^|[^A-Za-z])(HH|H|hh|h|i|ss|s|A|a)(?=$|[^A-Za-z])/;

/**
 * True if `format` (after stripping date tokens) contains a stand-alone
 * time-token character.
 *
 * @param {string} format
 * @returns {boolean}
 */
export function looksLikeTimeFormat(format) {
  return TIME_TOKEN_RE.test(stripDateTokens(format));
}

// ---------------------------------------------------------------------------
// Per-field sanitizers + ensureCompleteLocaleConfig.
//
// Each sanitizer returns either a valid value or `undefined`. The merge
// function uses `undefined` to mean "keep the default for this field" so
// invalid inputs cannot overwrite valid defaults with garbage.
// ---------------------------------------------------------------------------

/**
 * Trim-aware emptiness check. Whitespace-only strings (`'  '`) count as empty
 * and fall back to defaults. The original (non-trimmed) value passes through
 * unchanged when accepted — only the emptiness check uses trim.
 *
 * @param {unknown} v
 * @returns {v is string}
 */
function isNonEmptyString(v) {
  return typeof v === 'string' && v.trim() !== '';
}

/** @type {Record<string, (v: unknown) => unknown>} */
const SANITIZERS = {
  locale: (v) => isNonEmptyString(v) ? v : undefined,
  displayLocale: (v) => isNonEmptyString(v) ? v : undefined,
  dateFormat: (v) => isNonEmptyString(v) ? v : undefined,
  // timeFormat is now interpreted by the picker / renderDateTime — empty or
  // whitespace-only values fall back to LOCALE_DEFAULTS.timeFormat. datetimeFormat
  // remains stored-only metadata in current PR but is sanitized identically so
  // future interpretation work doesn't have to revisit loader semantics.
  timeFormat: (v) => isNonEmptyString(v) ? v : undefined,
  datetimeFormat: (v) => isNonEmptyString(v) ? v : undefined,
  firstDayOfWeek: (v) => v === 0 || v === 1 ? v : undefined,
  rtl: (v) => typeof v === 'boolean' ? v : undefined,
};

/**
 * Reject hostile shapes (null, primitives, arrays, Date instances) before
 * attempting the per-field merge.
 *
 * @param {unknown} cfg
 * @returns {boolean}
 */
function isCandidateLocaleConfig(cfg) {
  return cfg != null
    && typeof cfg === 'object'
    && !Array.isArray(cfg)
    && !(cfg instanceof Date);
}

/**
 * Merge a partial loader result over `LOCALE_DEFAULTS` so the picker always
 * receives a complete `LocaleConfig` with every field structurally valid.
 * Per-field merge: only sanitizer-approved values overwrite defaults.
 *
 * @param {unknown} cfg
 * @returns {import('../../contracts/locale.js').LocaleConfig}
 */
function ensureCompleteLocaleConfig(cfg) {
  if (!isCandidateLocaleConfig(cfg)) return LOCALE_DEFAULTS;
  const out = /** @type {Record<string, unknown>} */ ({ ...LOCALE_DEFAULTS });
  const src = /** @type {Record<string, unknown>} */ (cfg);
  for (const key of Object.keys(SANITIZERS)) {
    const sanitized = SANITIZERS[key](src[key]);
    if (sanitized !== undefined) out[key] = sanitized;
  }
  return /** @type {import('../../contracts/locale.js').LocaleConfig} */ (out);
}

// ---------------------------------------------------------------------------
// Snake→camel normalization for API responses. Empty/whitespace-only strings
// are rejected for ALL string fields — interpreted-config drift would let
// blank DB values blank out the editor/list display. Use isNonEmptyString
// (trim-aware) so '  ' counts as unset and falls back to LOCALE_DEFAULTS.
// ---------------------------------------------------------------------------

/**
 * Normalize an API-shaped locale row into the camelCase `LocaleConfig` shape.
 * Returns `null` for non-object input. Returns a partial object for valid
 * objects — fields not present (or invalid) are simply omitted, leaving
 * `ensureCompleteLocaleConfig` to fill in defaults.
 *
 * Exported for unit testing in isolation.
 *
 * @param {unknown} api
 * @returns {Partial<import('../../contracts/locale.js').LocaleConfig> | null}
 */
export function normalizeApiLocale(api) {
  if (api == null || typeof api !== 'object') return null;
  const src = /** @type {Record<string, unknown>} */ (api);
  /** @type {Partial<import('../../contracts/locale.js').LocaleConfig>} */
  const out = {};
  if (isNonEmptyString(src.locale)) {
    out.locale = src.locale;
  }
  if (isNonEmptyString(src.display_locale)) {
    out.displayLocale = src.display_locale;
  }
  if (isNonEmptyString(src.date_format)) {
    out.dateFormat = src.date_format;
  }
  if (isNonEmptyString(src.time_format)) {
    out.timeFormat = src.time_format;
  }
  if (isNonEmptyString(src.datetime_format)) {
    out.datetimeFormat = src.datetime_format;
  }
  if (src.first_day_of_week != null) {
    const n = Number(src.first_day_of_week);
    if (n === 0 || n === 1) out.firstDayOfWeek = /** @type {0 | 1} */ (n);
  }
  if (src.rtl != null) {
    out.rtl = src.rtl === 1 || src.rtl === '1' || src.rtl === true;
  }
  return out;
}

// ---------------------------------------------------------------------------
// createLocaleLoader — mirrors createBrandLoader.
// ---------------------------------------------------------------------------

/**
 * Create a locale loader. Resolves with a complete, sanitized `LocaleConfig`
 * regardless of whether the API endpoint exists or fails.
 *
 * `staticConfig` is JS-provided (compile-time defaults) and treated as
 * `Partial<LocaleConfig>` in camelCase. It is run through the per-field
 * sanitizer same as the API response — invalid static values cannot reach
 * runtime.
 *
 * @param {{
 *   apiClient?: { get(path: string, opts?: { signal?: AbortSignal }): Promise<unknown> },
 *   staticConfig?: Partial<import('../../contracts/locale.js').LocaleConfig>,
 *   logger?: { warn?: (message: string, ctx?: { data?: unknown }) => void },
 * }} [opts]
 * @returns {{ load(signal?: AbortSignal): Promise<import('../../contracts/locale.js').LocaleConfig> }}
 */
export function createLocaleLoader({ apiClient, staticConfig, logger } = {}) {
  return {
    async load(signal) {
      let config = LOCALE_DEFAULTS;
      if (staticConfig) {
        config = ensureCompleteLocaleConfig({ ...config, ...staticConfig });
      }
      if (apiClient) {
        try {
          const api = await apiClient.get('/settings/locale', { signal });
          const normalized = normalizeApiLocale(api);
          if (normalized) {
            config = ensureCompleteLocaleConfig({ ...config, ...normalized });
          }
        } catch {
          // Silent — use current config (defaults + static) on failure.
          // Matches brand-loader's silent failure policy.
        }
      }
      if (config.dateFormat && looksLikeTimeFormat(config.dateFormat)) {
        logger?.warn?.('locale.dateFormat contains a standalone time-token character; date formatting may be unexpected', {
          data: { dateFormat: config.dateFormat },
        });
      }
      return config;
    },
  };
}

// ---------------------------------------------------------------------------
// loadLocaleForBoot — single-timer, never-rejects, sync-throw-safe race
// between loader.load() and a hard timeout. Owns its own AbortController so
// the in-flight fetch is actually cancelled when the timeout wins.
// ---------------------------------------------------------------------------

/**
 * Race `loader.load()` against a timeout. Resolves with `LOCALE_DEFAULTS`
 * on any failure path (timeout, abort, loader rejection, sync throw,
 * hostile resolution shape). Never rejects.
 *
 * Invalid `timeoutMs` (non-finite, < 1) → defaults immediately, no fetch.
 * `timeoutMs > 60_000` is clamped to 60_000.
 *
 * Abort is timeout-only: happy-path and rejection-path don't abort because
 * there's nothing in-flight worth cancelling at that point.
 *
 * @param {{ load(signal?: AbortSignal): Promise<unknown> }} loader
 * @param {number} timeoutMs
 * @returns {Promise<import('../../contracts/locale.js').LocaleConfig>}
 */
export async function loadLocaleForBoot(loader, timeoutMs) {
  if (!Number.isFinite(timeoutMs) || timeoutMs < MIN_TIMEOUT_MS) {
    return LOCALE_DEFAULTS;
  }
  const effectiveTimeout = Math.min(timeoutMs, MAX_TIMEOUT_MS);

  const ctl = new AbortController();
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timerId;
  let settled = false;
  try {
    return await new Promise((resolve) => {
      /** @param {import('../../contracts/locale.js').LocaleConfig} value */
      const settle = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };
      timerId = setTimeout(() => {
        ctl.abort();
        settle(LOCALE_DEFAULTS);
      }, effectiveTimeout);
      // Promise.resolve().then wraps so a SYNCHRONOUS throw from
      // loader.load() becomes a rejection rather than escaping the
      // executor and rejecting the helper.
      Promise.resolve()
        .then(() => loader.load(ctl.signal))
        .then(
          (cfg) => settle(ensureCompleteLocaleConfig(cfg)),
          () => settle(LOCALE_DEFAULTS),
        );
    });
  } finally {
    clearTimeout(timerId);
  }
}
